import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import BackgroundImage from './../../assets/img/system_login_bg.png'
import PanasonicImage from './../../assets/logo/syslogin_panasonic_logo.png'
import Paper from '@mui/material/Paper';
import { useDispatch, useSelector } from 'react-redux';
import { userActions } from './../../redux/actions'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CopyrightText from '../../components/common/CopyrightText';
import { AUTH_USER_TYPES } from '../../constants/auth.constants';
import {
  DEFAULT_TENANT_LOGIN_CONTEXT_ID,
  getLoginContextById,
  TENANT_LOGIN_CONTEXTS,
} from '../../config/loginTenants'

export default function SignIn() {

  const dispatch = useDispatch()
  const isLoggingIn = useSelector((state) => state?.authentication?.loading)
  const loginErrorMessage = useSelector(
    (state) =>
      state?.authentication?.error?.error ||
      (typeof state?.authentication?.error === "string"
        ? state.authentication.error
        : "") ||
      ""
  )
  const [visibleLoginError, setVisibleLoginError] = useState("")

  useEffect(() => {
    if (!loginErrorMessage) {
      setVisibleLoginError("")
      return
    }

    setVisibleLoginError(loginErrorMessage)

    const timer = setTimeout(() => {
      setVisibleLoginError("")
    }, 6000)

    return () => clearTimeout(timer)
  }, [loginErrorMessage])

  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [loginType, setLoginType] = useState(null)
  const [loginContextId, setLoginContextId] = useState(DEFAULT_TENANT_LOGIN_CONTEXT_ID)
  const [forgotPasswordHint, setForgotPasswordHint] = useState(false)
  const [usernameFocused, setUsernameFocused] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [usernameAutofilled, setUsernameAutofilled] = useState(false)
  const [passwordAutofilled, setPasswordAutofilled] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const usernameRef = useRef(null)
  const passwordRef = useRef(null)

  const syncAutofillValues = useCallback(() => {
    const usernameValue = usernameRef.current?.value ?? ""
    const passwordValue = passwordRef.current?.value ?? ""

    if (usernameValue) {
      setUsername(usernameValue)
    }
    if (passwordValue) {
      setPassword(passwordValue)
    }
  }, [])

  const handleAutofillAnimation = (setAutofilled) => (event) => {
    if (event.animationName === "mui-auto-fill") {
      setAutofilled(true)
      syncAutofillValues()
    } else if (event.animationName === "mui-auto-fill-cancel") {
      setAutofilled(false)
    }
  }

  useEffect(() => {
    syncAutofillValues()
    const timers = [100, 300, 600, 1000].map((delay) =>
      setTimeout(syncAutofillValues, delay)
    )
    window.addEventListener("pageshow", syncAutofillValues)
    const handleFirstInteraction = () => syncAutofillValues()
    document.addEventListener("pointerdown", handleFirstInteraction, { once: true })

    return () => {
      timers.forEach(clearTimeout)
      window.removeEventListener("pageshow", syncAutofillValues)
      document.removeEventListener("pointerdown", handleFirstInteraction)
    }
  }, [syncAutofillValues])

  const handleSelectLoginType = (type) => {
    setVisibleLoginError("")
    setForgotPasswordHint(false)
    if (type === AUTH_USER_TYPES.SYSTEM) {
      setLoginType(AUTH_USER_TYPES.SYSTEM)
      setLoginContextId("system")
      return
    }
    setLoginType(AUTH_USER_TYPES.TENANT)
    setLoginContextId(DEFAULT_TENANT_LOGIN_CONTEXT_ID)
  }

  const handleBackToOptions = () => {
    setLoginType(null)
    setForgotPasswordHint(false)
    setVisibleLoginError("")
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const usernameValue = usernameRef.current?.value ?? username
    const passwordValue = passwordRef.current?.value ?? password
    const loginContext =
      loginType === AUTH_USER_TYPES.SYSTEM
        ? getLoginContextById("system")
        : getLoginContextById(loginContextId)
    dispatch(userActions.login(usernameValue, passwordValue, loginContext));
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={12}
        sm={6}
        md={6}
        sx={{
          backgroundImage: `url(${BackgroundImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(120deg, rgba(2,6,23,0.75) 0%, rgba(11,94,215,0.35) 100%)",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            alignItems: "flex-end",
            p: { xs: 3, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: 460, color: "white" }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              IDP Admin Console
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage identity, tenant operations, and platform-level administration in one place.
            </Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} md={6} component={Paper} elevation={0} square flexDirection={'column'}>
        <Box
          sx={{
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '95vh',
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "primary.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              mb: 2,
            }}
          >
            <LockOutlinedIcon />
          </Box>
          <Box
            component={"img"}
            src={PanasonicImage}
            alt="Alternate Text"
            sx={{
              position: 'relative',
              width: '180px'
            }}
            mb='20px'
          ></Box>
          <Typography
            component="h1"
            variant="h5"
            sx={{ mb: 1 }}
          >
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to continue to the admin portal.
          </Typography>
          {visibleLoginError ? (
            <Alert
              severity="error"
              sx={{ mt: 2, width: "100%", maxWidth: 420 }}
              onClose={() => setVisibleLoginError("")}
            >
              {visibleLoginError}
            </Alert>
          ) : null}
          {!loginType ? (
            <Box sx={{ mt: 2.5, width: "100%", maxWidth: 420 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  mb: 1.5,
                }}
              >
                LOGIN OPTIONS
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "background.paper",
                }}
              >
                <ListItemButton
                  onClick={() => handleSelectLoginType(AUTH_USER_TYPES.SYSTEM)}
                  sx={{ py: 1.75, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 56 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AdminPanelSettingsOutlinedIcon fontSize="small" />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary="System User"
                    secondary="Login as a system user to manage system-level resources and tenants."
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                  <ChevronRightIcon sx={{ color: "text.secondary" }} />
                </ListItemButton>
                <Divider />
                <ListItemButton
                  onClick={() => handleSelectLoginType(AUTH_USER_TYPES.TENANT)}
                  sx={{ py: 1.75, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 56 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "success.main",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ApartmentOutlinedIcon fontSize="small" />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary="Tenant User"
                    secondary="Login as a tenant admin to manage resources for a specific tenant."
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                  <ChevronRightIcon sx={{ color: "text.secondary" }} />
                </ListItemButton>
              </Box>
            </Box>
          ) : (
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              mt: 2.5,
              width: '100%',
              maxWidth: 420,
            }}
          >
            {loginType === AUTH_USER_TYPES.TENANT ? (
            <TextField
              select
              margin="normal"
              required
              fullWidth
              variant="outlined"
              id="loginAs"
              label="Tenant"
              value={loginContextId}
              onChange={(e) => setLoginContextId(e.target.value)}
            >
              {TENANT_LOGIN_CONTEXTS.map((context) => (
                <MenuItem key={context.id} value={context.id}>
                  {context.label}
                </MenuItem>
              ))}
            </TextField>
            ) : null}
            <TextField
              margin="normal"
              required
              fullWidth
              variant="outlined"
              id="username"
              label="Username"
              name="username"
              autoFocus
              autoComplete="username"
              inputRef={usernameRef}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value)
              }}
              onFocus={() => {
                setUsernameFocused(true)
                syncAutofillValues()
              }}
              onBlur={() => setUsernameFocused(false)}
              inputProps={{
                onAnimationStart: handleAutofillAnimation(setUsernameAutofilled),
              }}
              InputLabelProps={{
                shrink:
                  usernameFocused ||
                  username.length > 0 ||
                  usernameAutofilled,
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              variant="outlined"
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              inputRef={passwordRef}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
              }}
              onFocus={() => {
                setPasswordFocused(true)
                syncAutofillValues()
              }}
              onBlur={() => setPasswordFocused(false)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                onAnimationStart: handleAutofillAnimation(setPasswordAutofilled),
              }}
              InputLabelProps={{
                shrink:
                  passwordFocused ||
                  password.length > 0 ||
                  passwordAutofilled,
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoggingIn}
              sx={{ mt: 3, mb: 2, py: 1.1 }}
            >
              {isLoggingIn ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>
            {forgotPasswordHint ? (
              <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setForgotPasswordHint(false)}>
                Admin forgot-password API is not confirmed in the IDP collection yet. Please contact an administrator.
              </Alert>
            ) : null}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Link
                href="#"
                variant="body2"
                onClick={(event) => {
                  event.preventDefault()
                  handleBackToOptions()
                }}
              >
                Back
              </Link>
              <Link
                href="#"
                variant="body2"
                onClick={(event) => {
                  event.preventDefault()
                  setForgotPasswordHint(true)
                }}
              >
                Forgot password?
              </Link>
            </Box>
          </Box>
          )}
        </Box>
        <Box
          component="footer"
          sx={{
            py: 1,
            px: 1,
            mt: 'auto',
            textAlign: "center"
          }}
        >
          <Container maxWidth="sm">
            <CopyrightText />
          </Container>
        </Box>
      </Grid>
    </Grid>
  );
}